import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { LOGO } from "../../data";
import { scrollToId } from "./useLenis";

const line = {
    hidden: { y: "110%" },
    show: (i) => ({
        y: "0%",
        transition: { delay: 0.5 + i * 0.12, duration: 0.8, ease: [0.22, 1, 0.36, 1] },
    }),
};

const Sparkle = ({ className, delay, emoji }) => (
    <motion.span
        className={`absolute select-none pointer-events-none ${className}`}
        initial={{ opacity: 0, scale: 0 }}
        animate={{ opacity: [0, 1, 0.7, 1], scale: [0, 1, 0.9, 1], rotate: [0, 20, -10, 0] }}
        transition={{ delay, duration: 4, repeat: Infinity, repeatType: "reverse" }}
    >
        {emoji}
    </motion.span>
);

export const Hero = () => {
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({
        target: ref,
        offset: ["start start", "end start"],
    });
    const y = useTransform(scrollYProgress, [0, 1], [0, 160]);
    const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

    return (
        <section
            id="hero"
            ref={ref}
            data-testid="hero-section"
            className="relative min-h-[100svh] flex flex-col items-center justify-center overflow-hidden pt-24 pb-16 px-4 dot-grid"
            style={{
                background:
                    "radial-gradient(120% 90% at 50% 0%, #ffe1ee 0%, #ffd0e4 35%, #fff9fa 100%)",
            }}
        >
            <Sparkle className="top-[18%] left-[10%] text-3xl md:text-5xl" delay={0.4} emoji="✨" />
            <Sparkle className="top-[26%] right-[12%] text-4xl md:text-6xl" delay={0.9} emoji="🍓" />
            <Sparkle className="bottom-[22%] left-[16%] text-3xl md:text-5xl" delay={1.3} emoji="❤️" />
            <Sparkle className="bottom-[28%] right-[18%] text-2xl md:text-4xl" delay={0.7} emoji="✨" />
            <Sparkle className="top-[40%] left-[6%] text-2xl md:text-3xl" delay={1.6} emoji="🍓" />

            <motion.div style={{ y, opacity }} className="relative z-10 w-full max-w-4xl flex flex-col items-center text-center">
                <motion.img
                    src={LOGO.hero}
                    alt="¡Qué Fresas! logo"
                    data-testid="hero-logo"
                    initial={{ opacity: 0, scale: 0.8, y: -20 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
                    className="w-64 md:w-96 h-auto object-contain mb-8 drop-shadow-[0_10px_30px_rgba(233,30,140,0.25)]"
                />

                <span className="font-label uppercase tracking-[0.25em] text-xs md:text-sm text-hotpink font-semibold mb-4">
                    Plaza Cortes Leonesas · León
                </span>

                <h1 className="bubble-text font-display font-bold leading-[0.95] text-4xl sm:text-6xl lg:text-7xl">
                    {["¡El capricho", "más dulce", "de León! 🍓"].map((t, i) => (
                        <span key={i} className="block overflow-hidden py-1">
                            <motion.span
                                className="block"
                                custom={i}
                                variants={line}
                                initial="hidden"
                                animate="show"
                            >
                                {t}
                            </motion.span>
                        </span>
                    ))}
                </h1>

                <motion.p
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 1.1, duration: 0.7 }}
                    className="mt-6 max-w-xl text-base md:text-lg text-[#6e4b58] font-medium"
                >
                    Fresas con crema o helado, gofres bubble y smoothies — tú
                    eliges, nosotros creamos ✨
                </motion.p>

                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 1.3, duration: 0.7 }}
                    className="mt-9 flex flex-col sm:flex-row gap-4"
                >
                    <motion.button
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.96 }}
                        data-testid="hero-cta-menu"
                        onClick={() => scrollToId("menu")}
                        className="bg-hotpink text-white font-display font-semibold text-lg px-8 py-4 rounded-full shadow-xl shadow-hotpink/40"
                    >
                        Ver Menú 🍓
                    </motion.button>
                    <motion.button
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.96 }}
                        data-testid="hero-cta-location"
                        onClick={() => scrollToId("ubicacion")}
                        className="bg-white text-hotpink border-2 border-hotpink font-display font-semibold text-lg px-8 py-4 rounded-full shadow-lg shadow-hotpink/10"
                    >
                        Encuéntranos 📍
                    </motion.button>
                </motion.div>
            </motion.div>

            {/* Big bouncing strawberry */}
            <motion.div
                aria-hidden
                className="absolute bottom-6 md:bottom-10 text-6xl md:text-8xl z-10 select-none"
                animate={{ y: [0, -26, 0], rotate: [0, 8, -8, 0] }}
                transition={{ duration: 2.4, repeat: Infinity, ease: "easeInOut" }}
            >
                🍓
            </motion.div>
        </section>
    );
};
